import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
/* eslint-disable react/no-danger,@typescript-eslint/naming-convention */
import { Provider } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { Fragment, useCallback, useContext, useEffect, useRef, useMemo, useState, } from 'preact/compat';
import { ThemeProvider } from 'styled-components';
import useDidMount from 'beautiful-react-hooks/useDidMount';
import DOMPurify from 'dompurify';
import useGlobalEvent from 'beautiful-react-hooks/useGlobalEvent';
import { yupResolver } from '@hookform/resolvers/yup';
import { configureStore } from '@reduxjs/toolkit';
import BasketItems from '@basket/components/BasketItems';
import Notices from '@basket/components/Notices';
import convertDataAttributes from '@basket/convertDataAttributes';
import { basketStore } from '@basket/store';
import MiscBasketItems from '@basket/components/MiscBasketItems';
import { ConfigCTX, configDefaults } from '@basket/context/config';
import BasketOverview from '@basket/components/BasketOverview';
import PromoCode from '@basket/components/PromoCode';
import PaymentIcons from '@basket/components/PaymentIcons';
import UpsellList from '@basket/components/UpsellList';
import MediaSizes from '@widgets/shared/MediaSizes';
import { basketSchema } from '@basket/validation/schema';
import Notice from '@widgets/shared/components/Notice';
import { setTermsContent, setTermsHeader, setTermsSubheader } from '@basket/store/reducers/termsAndConditions';
import { useAppDispatch } from '@basket/store/hooks';
import useTotal from '@basket/components/BasketOverview/hooks/useTotal';
import BasketHeader from '@basket/components/BasketHeader';
const Element = () => {
    const { emptyTitle, emptyContent, totalExtraContent, upsellBreakpoint, submissionEndpoint, termsShowCheckbox, basketPage, isModal, emptyImage, emptyLink, paymentIcons, paymentCompleteTitle, paymentCompleteCopy, misc: initialMisc, items: initialItems, requiresSignature, } = useContext(ConfigCTX);
    const ref = useRef(null);
    const [paymentComplete, setPaymentComplete] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorModalContent, setErrorModalContent] = useState(null);
    // TODO: on form completed add the upsell to this array.
    const dispatch = useAppDispatch();
    const schema = basketSchema;
    const form = useForm({
        mode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            gift_voucher: '',
            Items: initialItems,
            Misc: initialMisc,
            Upsells: [],
            acceptTerms: !termsShowCheckbox,
            ...(requiresSignature) ? {
                SignatureDate: new Date().toISOString(),
                SignatureName: '',
            } : {},
        },
    });
    const showHeader = useMemo(() => isModal || window.innerWidth <= MediaSizes.XS_MAX, [isModal]);
    const onPaymentSuccess = useGlobalEvent('payment.success');
    const onPaymentError = useGlobalEvent('payment.error');
    const onBasketModify = useGlobalEvent('basket.modify');
    const [elementSize, setElementSize] = useState(ref.current ? ref.current.offsetWidth : 9999);
    useEffect(() => {
        if (ref.current) {
            setElementSize(ref.current.offsetWidth !== 0 ? ref.current.offsetWidth : 9999);
        }
    }, [ref]);
    window.addEventListener('resize', (e) => {
        setElementSize(ref.current ? ref.current.offsetWidth : 0);
    });
    const { handleSubmit, watch, setValue } = form;
    const { Items, Misc } = watch();
    // const onSubmit = useCallback((data: BasketSchema) => console.log(data), []);
    const total = useTotal(Items ?? [], Misc ?? []);
    onBasketModify((e) => {
        const { TermsHeading: termsHeading, TermsSubheading: termsSubheading, TermsContent: termsContent, Items: items, Misc: misc, } = e.detail;
        // console.log('Setting new basket to ', e);
        if (termsHeading) {
            dispatch(setTermsHeader(termsHeading));
        }
        if (termsSubheading) {
            dispatch(setTermsSubheader(termsSubheading));
        }
        if (termsContent) {
            dispatch(setTermsContent(termsContent));
        }
        setValue('Items', items);
        setValue('Misc', misc);
    });
    const mount = useCallback(() => {
        window.addEventListener('basket.modify', (e) => {
            const { detail } = e;
            const { Items: receivedItems, Misc: receivedMisc, } = detail;
            setValue('Items', receivedItems);
            setValue('Misc', receivedMisc);
        });
        // if this is not a modal, override the 'book now' button so it scrolls to overview
        if (!isModal) {
            window.dispatchEvent(new CustomEvent('primary.action.override', {
                detail: {
                    buttonText: 'Book Now',
                    buttonAction: () => {
                        const element = document.querySelector('#basket-content .overview');
                        element?.scrollIntoView({
                            block: 'start',
                            behavior: 'smooth',
                        });
                    },
                    buttonEnabled: true,
                },
            }));
        }
    }, [isModal, setValue]);
    useDidMount(mount);
    const handleErrorOpen = useCallback(() => setErrorModalOpen(true), [setErrorModalOpen]);
    const handleErrorClose = useCallback(() => setErrorModalOpen(false), [setErrorModalOpen]);
    onPaymentSuccess(() => setPaymentComplete(true));
    onPaymentError((event) => {
        setErrorModalContent(event.detail.errorMessage);
        handleErrorOpen();
    });
    const handleSubmitEvent = useCallback(() => {
        if (isModal) {
            window.location.href = basketPage ?? 'basket.html';
        }
        else {
            if (!(ref && ref.current)) {
                return;
            }
            // console.log('Form: ', ref.current);
            ref.current.submit();
        }
    }, [isModal, basketPage]);
    const basketEmpty = useMemo(() => (!Items || !Items.length) && (!Misc || !Misc.length), [Items, Misc]);
    if (basketEmpty) {
        return (_jsxs(Fragment, { children: [showHeader && (_jsx(BasketHeader, {})), _jsxs("div", { className: "basket__empty slab slab--centered", children: [emptyImage && (_jsx("picture", { className: "basket__empty-image", children: _jsx("img", { src: emptyImage.src, alt: emptyImage.alt ?? '' }) })), _jsx("div", { className: "slab__intro", children: _jsx("span", { className: "h3", children: emptyTitle }) }), emptyContent && (_jsx("div", { className: "slab__content", dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(emptyContent) } })), emptyLink && (_jsx("a", { className: "btn btn--primary btn--full", href: emptyLink.link, target: "_self", children: emptyLink.text }))] })] }));
    }
    return (_jsxs(Fragment, { children: [showHeader && (_jsx(BasketHeader, {})), _jsx(Notices, {}), (errorModalOpen && errorModalContent) && (_jsx(Notice, { onClose: handleErrorClose, content: errorModalContent })), _jsx(FormProvider, { ...form, children: _jsxs("form", { ref: ref, action: submissionEndpoint ?? '/basket.html', method: "POST" // modal page should just redirect to basket
                    , className: "basket", onSubmit: handleSubmit(handleSubmitEvent), noValidate: true, children: [_jsx("section", { className: "basket__main", children: _jsxs("ul", { className: "basket__items items", children: [_jsx(BasketItems, {}), _jsx(MiscBasketItems, {}), elementSize >= upsellBreakpoint && (_jsx(UpsellList, {}))] }) }), _jsxs("aside", { className: "basket__overview", children: [!paymentComplete && (_jsxs(_Fragment, { children: [_jsx(BasketOverview, { items: Items, misc: Misc, totalExtraContent: totalExtraContent, layout: "basket", total: total }), elementSize < upsellBreakpoint && (_jsx(UpsellList, {})), _jsx(PromoCode, { title: "Gift Voucher or Promo Code", copy: "You\u2019ve got a voucher or code? Use it here!", total: total }), paymentIcons && (_jsx(PaymentIcons, { icons: paymentIcons }))] })), (paymentComplete && paymentCompleteTitle) && (_jsxs("div", { className: "payment-complete", children: [_jsx("span", { className: "h3 payment-complete__title", children: paymentCompleteTitle }), paymentCompleteCopy && (_jsx("span", { className: "payment-complete__copy", children: paymentCompleteCopy }))] }))] })] }) })] }));
};
function Basket(props) {
    const { dataset } = props;
    const formattedAttributes = convertDataAttributes(dataset ?? {});
    const basketOptions = useMemo(() => ({
        ...configDefaults,
        ...formattedAttributes,
    }), [formattedAttributes]);
    const store = configureStore({
        ...basketStore,
    });
    return (_jsx(Provider, { store: store, children: _jsx(ThemeProvider, { theme: (formattedAttributes.theme ?? {}), children: _jsx(ConfigCTX.Provider, { value: basketOptions, children: _jsx(Element, {}) }) }) }));
}
export default Basket;
